import React, { useState } from 'react';
import { CalendarPlus, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/common/Button';

interface BankHolidayFormProps {
  onSubmit: (data: { title: string; date: string }) => void;
  isSubmitting?: boolean;
}

export const BankHolidayForm: React.FC<BankHolidayFormProps> = ({
  onSubmit,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    date: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        title: formData.title.trim(),
        date: formData.date,
      });
      // Reset form
      setFormData({ title: '', date: '' });
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Get today's date for min attribute
  const today = new Date().toISOString().split('T')[0];

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <CalendarPlus className="w-5 h-5 text-blue-500" />
        Add Bank Holiday
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Holiday Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            className={`form-input w-full ${errors.title ? 'border-red-500' : ''}`}
            placeholder="e.g., Christmas Day"
            disabled={isSubmitting}
          />
          {errors.title && (
            <p className="text-red-500 text-sm mt-1">{errors.title}</p>
          )}
        </div>

        {/* Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => handleChange('date', e.target.value)}
            className={`form-input w-full ${errors.date ? 'border-red-500' : ''}`}
            disabled={isSubmitting}
          />
          {errors.date && (
            <p className="text-red-500 text-sm mt-1">{errors.date}</p>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
        <AlertCircle className="w-4 h-4" />
        <span>Bank holidays are excluded when calculating working days for holiday requests.</span>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          type="submit"
          icon={<CalendarPlus className="w-4 h-4" />}
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          Add Bank Holiday
        </Button>
      </div>
    </form>
  );
};

export default BankHolidayForm;
